import React from "react"
import { makeStyles } from "tss-react/mui"
import { Divider, Grid, Stack, Typography } from "@mui/material"
import { TimelineCalendarEventTimeRange } from "../../../createOrder/components/resource/calendar/fullCalendarTypes"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { MeetingDetailsTableSetting } from "./MeetingDetailsTableSetting"
import { TimeRangePicker } from "./TimeRangePicker"
import { ResourceItem, SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import {
    BookingConfig,
    DynamicConfigField,
    DynamicConfigKey,
    ExtendedDynamicConfigKey,
} from "../../../createOrder/components/form/formTypes"
import { DynamicFormField, DynamicFormFieldGroupWrap } from "../../../createOrder/components/form/DynamicFormField"
import { isRenderableConfigField } from "../../../createOrder/components/form/helpers"
import { ResponsibleAutocomplete } from "../../../createOrder/components/form/ResponsibleAutocomplete"
import { WorkgroupSelect } from "../../../createOrder/components/drawer/meetingDetails/WorkgroupSelect"

const useStyles = makeStyles()((theme) => ({
    wrap: {
        color: theme.palette.tailwind.slate[950],
    },
    input: {
        "& .MuiInputBase-root": {
            borderRadius: ".75rem",
        },
    },
}))

type MeetingDetailsProps = Readonly<{
    config: BookingConfig
    resourcesList: ResourceItem[]
    timeRange?: TimelineCalendarEventTimeRange
    user: SelectedMainUserState
    isLockedForOtherUser?: boolean
}>

const LOCKED_FOR_OTHER_USER_KEYS: readonly ExtendedDynamicConfigKey[] = [
    DynamicConfigKey.MEETING_NAME,
    DynamicConfigKey.PRIVATE_MEETING,
]

// Rendered elsewhere in the dialog (ResourcesInfo, the table-setup block, the participants step).
const SKIPPED_KEYS: readonly ExtendedDynamicConfigKey[] = [
    DynamicConfigKey.SERVICE_CENTER_MESSAGE,
    DynamicConfigKey.TABLE_ARANGEMENTS,
    DynamicConfigKey.ADD_PARTICIPANTS_FORM,
]

const isRenderableGroupField = (field: DynamicConfigField): boolean =>
    !SKIPPED_KEYS.includes(field.key) && isRenderableConfigField(field)

export function MeetingDetails({ resourcesList, timeRange, user, config, isLockedForOtherUser }: MeetingDetailsProps) {
    const { classes } = useStyles()

    const resourcesWithTableSetups = resourcesList.filter((resource) => resource.tableSetups)

    const allGroups = config.sections.flatMap(({ groups }) => groups)

    // Groups whose fields are all skipped or hidden must not leave an empty title/divider behind.
    const groups = allGroups
        .map((group) => ({ ...group, fields: group.fields.filter(isRenderableGroupField) }))
        .filter(({ fields }) => fields.length > 0)

    const hasTableArrangements = allGroups.some((group) =>
        group.fields.some(
            (field) => field.key === DynamicConfigKey.TABLE_ARANGEMENTS && isRenderableConfigField(field),
        ),
    )

    // Workgroup follows the responsible field; without one in the config it closes the first group.
    const hasResponsibleField = groups.some(({ fields }) =>
        fields.some(({ key }) => key === DynamicConfigKey.MEETING_RESPONSIBLE),
    )

    return (
        <Grid container flexDirection="column" gap="1.5rem" className={classes.wrap}>
            {timeRange && <TimeRangePicker timeRange={timeRange} />}

            {groups.map(({ fields, title }, groupIndex) => {
                const hasCustomComponent = fields.some(({ key }) => key === DynamicConfigKey.MEETING_RESPONSIBLE)

                return (
                    <React.Fragment key={groupIndex}>
                        {groupIndex > 0 && <Divider flexItem />}
                        {title && !hasCustomComponent && <Typography variant="subtitle1">{title}</Typography>}

                        {fields.map((config) => {
                            const { key, label, description } = config

                            if (key === DynamicConfigKey.MEETING_RESPONSIBLE) {
                                return (
                                    <React.Fragment key={key}>
                                        <DynamicFormFieldGroupWrap config={config}>
                                            <ResponsibleAutocomplete
                                                name={DynamicConfigKey.MEETING_RESPONSIBLE}
                                                label={label || description}
                                                user={user}
                                            />
                                        </DynamicFormFieldGroupWrap>
                                        <WorkgroupSelect />
                                    </React.Fragment>
                                )
                            }

                            return (
                                <DynamicFormField
                                    config={config}
                                    user={user}
                                    key={key}
                                    disabled={Boolean(isLockedForOtherUser) && LOCKED_FOR_OTHER_USER_KEYS.includes(key)}
                                />
                            )
                        })}
                        {!hasResponsibleField && groupIndex === 0 && <WorkgroupSelect />}
                    </React.Fragment>
                )
            })}
            {groups.length === 0 && <WorkgroupSelect />}

            {hasTableArrangements && resourcesWithTableSetups.length > 0 && (
                <>
                    <Divider />
                    <Stack gap="1rem">
                        <LocalizedStrict id="create-order-confirm-booking-table-setup">
                            <Typography variant="subtitle1">Table setup</Typography>
                        </LocalizedStrict>
                        <MeetingDetailsTableSetting resourcesList={resourcesWithTableSetups} />
                    </Stack>
                </>
            )}
        </Grid>
    )
}
